import {
  ABTestContextAwareConfigurationValue,
  AemConfigurationThirdpartySocialLoginProvider,
  MainWebShop,
} from '@as-react/common-types';
import type { MainWebShop as MainWebShopConfig } from '@as-react/common-types/configuration-service';
import { generateAemConfiguration } from '@as-react/common-utils';

/**
 * Per-webshop configuration used by mock handlers.
 * Each entry contains the AEM page config and MainWebShopConfig overrides
 * that differ from the shared defaults.
 */

export interface AemPageConfig {
  locale: string;
  translationLang: string;
  fictiveWebShop: string;
  mainWebShop: string;
  version: string;
  canonicalUrl: string;
  currency: string;
}

export interface WebshopMockConfig {
  aemPageConfig: AemPageConfig;
  mainWebShopConfig: MainWebShopConfig;
}

export const mockAemConfiguration = generateAemConfiguration({
  version: '2.41.0-RC18',
  market: 'be',
  mainWebShop: MainWebShop.ASADVENTURE,
  fictiveWebShop: '95',
  shopNumber: '',
  currency: '€',
  root: '/nl/',
  rootWithLanguageSuffix: '/nl',
  defaultCountryCode: 'be',
  rootWithoutFinalSlash: '/nl',
  canonicalUrl: 'https://acc-asadventure-com.foxandcat.eu/nl/lister.html',
  basketOverviewUrl: 'https://acc-asadventure-com.foxandcat.eu/nl/checkout/basket-overview.html',
  isEditor: false,
  elasticsearchIndex: 'www-asadventure-com_nl',
  lang: 'nl',
  locale: 'nl_NL',
  translationLang: 'nl',
  languageAnalytics: 'nl',
  isInAuthorMode: false,
  isSingleLanguageSite: false,
  isShopLanding: false,
  shoplanding: {
    timeout_reset_seconds: 1800,
  },
  buy: { delivery: { maxStores: 10, maxPickupPoints: 5 } },
  isTransactionalWebShop: true,
  isExploreMore: true,
  glossaryPath: 'https://acc-asadventure-com.foxandcat.eu/nl/glossary',
  fredhopperGlossaryPath: 'https://acc-asadventure-com.foxandcat.eu/nl/fredhopper-glossary',
  sizeGuidePath: 'https://acc-asadventure-com.foxandcat.eu/nl/size-guides',
  oauth: {
    clientId: '123',
  },
  pageInfo: {
    isCategoryPage: false,
    isBrandAsCategoryPage: false,
    isBrandPage: false,
    isPDPPage: true,
    isListerPage: false,
    isBasketOverviewPage: false,
    isHomePage: false,
    strippedUrl: '/nl/lister',
    isBuyPage: false,
    isSearchPage: false,
    isGiftCardPage: false,
    isGiftVoucherPage: false,
    isConfirmationPage: false,
    isWishlistPage: false,
    isNotFoundPage: false,
    pdpTemplateName: 'default',
  },
  alternateUrl: {
    en: 'https://acc-asadventure-com.foxandcat.eu/en/lister.html',
    fr: 'https://acc-asadventure-com.foxandcat.eu/fr/lister.html',
    nl: 'https://acc-asadventure-com.foxandcat.eu/nl/lister.html',
  },
  referralLinkParameter: 'invite',
  services: {
    api: '/',
    fredHopper: {
      suggest: '/api/search/suggest?search=',
      search: '/api/aem/search',
      scanner_search: '/api/aem/search/shoplanding/scanner',
      compare: '/api/search/be/search/compare',
    },
    graphql: '/graphql/execute.json/platform-asadventure',
  },
  servicesApi: '/',
  servicesFredHopperSuggest: '/api/search/suggest?search=',
  servicesFredHopperSearch: '/api/aem/search',
  servicesFredHopperCompare: '/api/search/be/search/compare',
  brandsPopularOverview: true,
  checkoutCc: true,
  checkoutHideInitialDeliveryOptions: false,
  checkoutLoyaltyCardPrefix: 'cus',
  deliveryPromises: {
    mostImportantDeliveryOption: ['HOME_DELIVERY', 'NEXT_DAY'],
  },
  brandImageCdnUrl: '/content/dam/be/brands/',
  unsubscribeUrl: 'url',
  servicesApiPrefixAemServices: '/api/',
  showStoreDetailsCoordinates: false,
  hideInNavigation: true,
  countryFlags: '/content/dam/general/flags/flag-',
  enableFullVatForm: true,
  invoiceHideCompany: false,
  limitationSearchResults: '3',
  thirdparty: {
    zopim: {
      enable: 'true',
      windowSize: 'small',
      showBadge: 'false',
      badgeColor: '#ffffff',
      badgeLayout: 'image_left',
      themeColor: '#F59100',
    },
    googleMaps: {
      defaultBounds: {
        southWestCorner: {
          lat: '48.52862485737759',
          lng: '-0.7378330078124691',
        },
        northEastCorner: {
          lat: '52.03662447812768',
          lng: '9.776083007812531',
        },
      },
      defaultZoom: 7,
      regionCode: 'BE',
      addressPopup: true,
      key: '',
    },
    postNl: {
      pId: '',
    },
    qTip: {
      tipSize: 16,
      closeButton: false,
    },
    facebook: {
      appId: '529479050785076',
    },
    socialLogin: {
      enabledList: [AemConfigurationThirdpartySocialLoginProvider.FACEBOOK],
    },
    klarna: {
      clientId: '38489442-7a01-59b0-ace6-a7b62d40e64d',
      onSiteMessagingScriptUrl: 'https://eu-library.klarnaservices.com/lib.js',
      onSiteMessagingEnvironment: 'playground',
      paymentsScriptUrl: 'https://x.klarnacdn.net/kp/lib/v1/api.js',
    },
    saferpay: {
      fields: {
        accessToken: '607bf3f9-7f27-409c-9be2-ecc4544407c0',
        apiUrl: 'https://www.saferpay.com/Fields/284988',
        scriptUrl: 'https://www.saferpay.com/Fields/lib/1.2/saferpay-fields.js',
      },
      deviceFingerPrinting: {
        dfpBaseUrl: 'https://dfp.secured-by-ingenico.com/mdt.js',
        instanceId: 'f60f672b-0741-48a0-9eed-99cd7f238b0d',
      },
    },
    applePay: {
      merchantId: 'merchant.preprod.worldline.com',
    },
    zitcha: {
      isEnabled: false,
    },
  },
  product: {
    images: {
      thumbnailBaseUrl: '//productimage011.asadventure.com/productimages/88x88/',
      smallBaseUrl: '//productimage011.asadventure.com/productimages/small/',
      largeBaseUrl: '//productimage011.asadventure.com/productimages/big/',
      cdnDomains: {
        activated: false,
        domains:
          'https://productimage011.asadventure.com/,https://productimage012.asadventure.com/,https://productimage013.asadventure.com/,https://productimage014.asadventure.com/',
        domain: '//productimage011.asadventure.com/',
        pathBaseProductDetailLarge: 'productimages/big/',
        pathBaseSwatch: 'productimages/swatch/',
        pathBaseTile: 'productimages/285x285/',
        pathBaseNewDesignTile: 'productimages/285x285/',
        pathBaseTileLocation: 'productimages/',
      },
    },
    swatch: {
      maxColors: 5,
      maxColorsMobile: 3,
      visible: true,
      saleVisible: true,
      saleLabel: '%',
      actionLabel: '%',
    },
    hideSpaceForRecommendedRetailPrice: true,
    code: {
      oneSize: '.',
    },
    manual: {
      cdnDomain: 'http://static.asadventure.com/',
      location: 'documents/',
    },
    video: {
      overlayLocation: '/content/dam/asadventure/general/video-overlay.png',
    },
    price: {
      showStartingFromLabel: 'true',
    },
    voucher: {
      productCode: 'Y121XX2002',
      productSize: '10',
    },
  },
  giftCard: {
    values: {
      min: '5',
      max: '1000',
      productSize: '10',
    },
  },
  storeCheckDefaultLocation: {
    lat: '50.8427501',
    lng: '4.351549900000009',
  },
  feature_toggles: {
    account_datalist_element: true,
    active_basket_reminder: false,
    bpost_address_validation: false,
    categorized_product_features: false,
    delivery_promise_select_size_label: false,
    delivery_promise_standard_delivery_label: false,
    show_legals: false,
    lister_in_store_filter: false,
    lister_colour_swatches_visible_mobile: false,
    lister_colour_swatches_visible_desktop: false,
    lister_show_wishlist_on_hover: false,
    lister_json_ld: false,
    lowest_price_all: false,
    our_planet: true,
    product_new_label: false,
    product_gender_icon: true,
    product_compare: true,
    product_online_exclusive: false,
    show_sticky_bar_pdp: false,
    stores_own_fascia_first: false,
    ecocheques_cart_item_message: false,
    visualize_both_delivery_promises: ABTestContextAwareConfigurationValue.DISABLED,
    no_basket_popup_on_click: false,
    enable_pdp_andes: ABTestContextAwareConfigurationValue.DISABLED,
    enable_pdp_carousel: ABTestContextAwareConfigurationValue.DISABLED,
    use_new_auth_flow: false,
  },
  seo: {
    productListerSEOTextLocation: 'top',
  },
  lister: {
    sort: {
      properties: [
        {
          key: 'bestselling',
          seq: 0,
          value: 'product.lister.sort.bestselling',
        },
        {
          key: 'price,asc',
          seq: 1,
          value: 'product.lister.sort.price.asc',
        },
        {
          key: 'price,desc',
          seq: 2,
          value: 'product.lister.sort.price.desc',
        },
        {
          key: 'brand,asc',
          seq: 3,
          value: 'product.lister.sort.brand.asc',
        },
        {
          key: 'brand,desc',
          seq: 4,
          value: 'product.lister.sort.brand.desc',
        },
        {
          key: 'be_v_saving_percentage,desc',
          seq: 5,
          value: 'product.lister.sort.be_v_saving_percentage.desc',
        },
        {
          key: 'be_new_product,desc',
          seq: 6,
          value: 'product.lister.sort.be_new_product.desc',
        },
      ],
      defaultValue: 'bestselling',
    },
    filter: {
      visible: false,
    },
    fredHopper: {
      redirect: true,
    },
  },
  review: {
    enable: true,
    listerEnable: true,
    amount: '3',
    reevoo: false,
    reevooAccountCode: 'none',
    reevooShowBrand: true,
    reevooStarsEnabled: true,
  },
  glossaryBoxPosition: 'bottom center',
  glossaryArrowPosition: 'top center',
  placeholders: {
    phoneNumbers: {
      phone: '+3238281264',
      mobilephone: '+32474123456',
    },
    vat: 'BE 0123 132 333',
  },
  activeEnvironment: 'tst',
  monetateUrl: 'monetateUrl',
  sizebayScript: 'sizebayScript',
  imageCdnPrefix: '',
  brandPagePathPrefix: 'brandPagePathPrefix',
  DPEPath: 'DPEPath',
  businessUnitId: 'businessUnitId',
  privacyPolicyLink: 'privacyPolicyLink',
  countryCodes: 'countryCodes',
  analyticsPageType: 'analyticsPageType',
  generalConfigUrl: '',
  pageUrls: {
    basketOverview: '',
    finishRegistration: '',
    privacyPolicy: '',
    wishlist: '',
  },
});

const mockMainWebShopConfig: MainWebShopConfig = {
  mainWebshopId: 1,
  businessUnitId: 1,
  businessUnit: 'AS Adventure',
  market: 'BE',
  marketId: 1,
  name: MainWebShop.ASADVENTURE,
  displayName: 'AS Adventure',
  fasciaId: 1,
  fascia: MainWebShop.ASADVENTURE,
  code: 'ASA',
  ttlBasketReservation: 3600,
  phoneNumber: '0800 12345',
  alwaysShowProducts: false,
  showMultiFasciaOnPdp: false,
  urls: { '95': 'https://www.asadventure.be' },
  baseUrlImages: 'https://images.asadventure.be',
  reviewProviderOverride: '',
};

function createConfig(
  aemOverrides: Partial<AemPageConfig>,
  mainWebShopOverrides: Partial<MainWebShopConfig>
): WebshopMockConfig {
  return {
    aemPageConfig: { ...mockAemConfiguration, ...aemOverrides },
    mainWebShopConfig: { ...mockMainWebShopConfig, ...mainWebShopOverrides },
  };
}

export const webshopConfigs: Record<string, WebshopMockConfig> = {
  asadventure: createConfig({}, {}),

  bever: createConfig(
    {
      locale: 'nl_NL',
      translationLang: 'nl',
      fictiveWebShop: '143',
      mainWebShop: MainWebShop.BEVER,
      canonicalUrl: 'https://www.bever.nl',
      currency: 'EUR',
    },
    {
      mainWebshopId: 2,
      businessUnitId: 2,
      businessUnit: 'Bever',
      market: 'NL',
      marketId: 2,
      name: MainWebShop.BEVER,
      displayName: 'Bever',
      fasciaId: 2,
      fascia: MainWebShop.BEVER,
      code: 'BVR',
      urls: { '143': 'https://www.bever.nl' },
      baseUrlImages: 'https://images.bever.nl',
    }
  ),

  cotswold: createConfig(
    {
      locale: 'en_GB',
      translationLang: 'en',
      fictiveWebShop: '62',
      mainWebShop: MainWebShop.COTSWOLD,
      canonicalUrl: 'https://www.cotswoldoutdoor.com',
      currency: 'GBP',
    },
    {
      mainWebshopId: 3,
      businessUnitId: 3,
      businessUnit: 'Cotswold Outdoor',
      market: 'GB',
      marketId: 3,
      name: MainWebShop.COTSWOLD,
      displayName: 'Cotswold Outdoor',
      fasciaId: 3,
      fascia: MainWebShop.COTSWOLD,
      code: 'CWO',
      urls: { '62': 'https://www.cotswoldoutdoor.com' },
      baseUrlImages: 'https://images.cotswoldoutdoor.com',
    }
  ),

  snowandrock: createConfig(
    {
      locale: 'en_GB',
      translationLang: 'en',
      fictiveWebShop: '210',
      mainWebShop: MainWebShop.SNOWANDROCK,
      canonicalUrl: 'https://www.snowandrock.com',
      currency: 'GBP',
    },
    {
      mainWebshopId: 4,
      businessUnitId: 3,
      businessUnit: 'Snow+Rock',
      market: 'GB',
      marketId: 3,
      name: MainWebShop.SNOWANDROCK,
      displayName: 'Snow+Rock',
      fasciaId: 4,
      fascia: MainWebShop.SNOWANDROCK,
      code: 'SNR',
      urls: { '210': 'https://www.snowandrock.com' },
      baseUrlImages: 'https://images.snowandrock.com',
    }
  ),

  runnersneed: createConfig(
    {
      locale: 'en_GB',
      translationLang: 'en',
      fictiveWebShop: '212',
      mainWebShop: MainWebShop.RUNNERSNEED,
      canonicalUrl: 'https://www.runnersneed.com',
      currency: 'GBP',
    },
    {
      mainWebshopId: 5,
      businessUnitId: 3,
      businessUnit: 'Runners Need',
      market: 'GB',
      marketId: 3,
      name: MainWebShop.RUNNERSNEED,
      displayName: 'Runners Need',
      fasciaId: 5,
      fascia: MainWebShop.RUNNERSNEED,
      code: 'RNR',
      urls: { '212': 'https://www.runnersneed.com' },
      baseUrlImages: 'https://images.runnersneed.com',
    }
  ),

  juttu: createConfig(
    {
      locale: 'nl_BE',
      translationLang: 'nl',
      fictiveWebShop: '224',
      mainWebShop: MainWebShop.JUTTU,
      canonicalUrl: 'https://www.juttu.be',
      currency: 'EUR',
    },
    {
      mainWebshopId: 6,
      businessUnitId: 1,
      businessUnit: 'Juttu',
      market: 'BE',
      marketId: 1,
      name: MainWebShop.JUTTU,
      displayName: 'Juttu',
      fasciaId: 6,
      fascia: MainWebShop.JUTTU,
      code: 'JTU',
      urls: { '224': 'https://www.juttu.be' },
      baseUrlImages: 'https://images.juttu.be',
    }
  ),

  ayacucho: createConfig(
    {
      locale: 'nl_BE',
      translationLang: 'nl',
      fictiveWebShop: '280',
      mainWebShop: MainWebShop.AYACUCHO,
      canonicalUrl: 'https://www.ayacucho.be',
      currency: 'EUR',
    },
    {
      mainWebshopId: 7,
      businessUnitId: 1,
      businessUnit: 'Ayacucho',
      market: 'BE',
      marketId: 1,
      name: MainWebShop.AYACUCHO,
      displayName: 'Ayacucho',
      fasciaId: 7,
      fascia: MainWebShop.AYACUCHO,
      code: 'AYA',
      urls: { '280': 'https://www.ayacucho.be' },
      baseUrlImages: 'https://images.ayacucho.be',
    }
  ),
};
